/**
 * This slice is mostly dedicated to CRM API data
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { CallStatus, CurrencyEnum } from '../../enums';
import { GenericStatusByCall } from '../../enums/status-by-call';
import lang from '../../language';
import { CALLS_TO_MEMOIZE } from '../../libSettings';
import {
  AutoPassRequest,
  CouponPreviewResponse,
  CreatePlanPaymentResponse,
  DetailedAccount,
  DetailedAccounts,
  CryptoRawData,
  LegalDeclaration,
  ModifyOwnBirthdateRequest,
  SearchTradingPlansRequest,
  TradingPlan,
  TradingPlatform,
  CreateReferralPayoutRequest,
  ReferralCommissionEntry,
  ReferralPagedResponse,
  ReferralPayoutRequest,
  ReferralProfile,
  ReferralProfileResponse,
  ReferralQrCode,
  ReferralReferredUser,
} from '../../models/crm/types';
import {
  ENROLL_METHOD,
  ENROLL_REQUEST_STATE,
  EnrollCRMState,
  IndividualExtendedInfoData,
} from '../../models/enroll';
import { getDemoTradingAccount, isDemoAccountReference } from '../../util/account-helpers';
import { extractDataNameFromGetCall } from '../../util/DataHelpers';
import { appendToErrorsLog } from '../../util/error-handling/ErrorHandlingHelpers';
import { isCallStatusReady, setCallStatus } from '../../util/error-handling/StatusByCallHelpers';
import { NullableNumber, NullableString } from '../../util/types';
import { AccountHistory, EMPTY_ACCOUNT_HISTORY, toAccountHistory } from '../account-flow/history';
import {
  accountsFailed,
  ErrorPayload,
  ErrorPayloadFull,
  getAuthUserTradingAcount,
  getCountryListSuccess,
  getIndividualExtendedInfo,
  individualExtendedInfoFailed,
  individualExtendedInfoSuccess,
  logout,
} from '../common-actions';
import { processErrorInReducer } from '../helpers';
import {
  toDetailedAccount,
  toReferralProfile,
  toRequestedPayoutAmount,
  toTradingPlan,
} from './stripping';
import {
  getLatestTradingAccount,
  hasAccountOnlyDemoPlan,
  parseOnboardingErrorCode,
  setCRMCallStatus,
} from './helpers';
import { AccountsCompletedPayload, CRMCall, LegalDeclarations } from './types';

export interface CRMState {
  connected: boolean;
  individualOrCompanyEmployeeId: number | null;
  individualExtendedInfo: IndividualExtendedInfoData | null;
  selectedAccount: number;
  tradingAccounts: string[];
  cashAccounts: string[];
  detailedAccounts: DetailedAccounts;
  accountHistory: AccountHistory;
  isUseDemoAccount: boolean;
  /** Reference id of the trading account the accounts call returned as still being created. */
  pendingTradingAccountReference: NullableString;
  isSwitchingAccount: boolean;
  isAutoPassEnabled: boolean;
  /**
   * tradingPlatformId value cases:
   * (1) has a value - call succeeded.
   * (2) is `undefined` - then call is pending.
   * (3) is `null` - then call failed (timeout or error).
   */
  tradingPlatformId: NullableNumber;
  /**
   * Publicly accessible (no token needed)
   */
  enroll: EnrollCRMState;
  error: string | null;
  errorData: any;
  errorsLog: string[];
  statusByCall: GenericStatusByCall<CRMCall>;
  files: { [fileName: string]: string };
  tradingPlans: TradingPlan[];
  planDetailsFlow: TradingPlan[];
  paymentResponseData: {
    gateway_url: NullableString;
    payment_reference: NullableString;
    data: NullableString;
    raw_data: CryptoRawData | null;
  };
  couponPreviewData: CouponPreviewResponse | null;
  couponPreviewError: string | null;
  pendingPurchasedPlanId: number | null;
  referralProfile: ReferralProfile | null;
  /**
   * Whether `own/profile` has come back at all this session, either way. Kept apart from
   * `referralProfile`, which is null both before the first call and after a failed one.
   */
  isReferralProfileResolved: boolean;
  referralQrCode: ReferralQrCode | null;
  referredUsers: ReferralReferredUser[];
  referralCommissions: ReferralCommissionEntry[];
  requestedPayoutAmount: NullableNumber;
}

export const INITIAL_STATE: CRMState = {
  connected: false,
  individualExtendedInfo: null,
  selectedAccount: 0,
  individualOrCompanyEmployeeId: null,
  isUseDemoAccount: false,
  pendingTradingAccountReference: null,
  isSwitchingAccount: false,
  tradingAccounts: [],
  cashAccounts: [],
  detailedAccounts: { cashAccounts: [], tradingAccounts: [] },
  accountHistory: EMPTY_ACCOUNT_HISTORY,
  isAutoPassEnabled: false,
  tradingPlatformId: null,
  enroll: {
    bankAccountDetailsOnCreate: {
      account_info: {
        bank_name: '',
        bank_country_id: '',
        bic: '',
        iban: '',
        currency_code: '',
      },
    },
    initiallySelectedPlanId: null,
    initiallySelectedAutoPass: undefined,
    initiallySelectedReferralCode: null,
  } as EnrollCRMState,
  error: null,
  errorData: null,
  errorsLog: [],
  statusByCall: {},
  files: {},
  tradingPlans: [],
  planDetailsFlow: [],
  paymentResponseData: {
    gateway_url: null,
    payment_reference: null,
    data: null,
    raw_data: null,
  },
  couponPreviewData: null,
  couponPreviewError: null,
  pendingPurchasedPlanId: null,
  referralProfile: null,
  isReferralProfileResolved: false,
  referralQrCode: null,
  referredUsers: [],
  referralCommissions: [],
  requestedPayoutAmount: null,
};

const crmSlice = createSlice({
  name: 'crm',
  initialState: INITIAL_STATE,
  reducers: {
    getReferralOwnProfile(state) {
      setCallStatus(
        CRMCall.getReferralOwnProfile,
        'crm/getReferralOwnProfile',
        CallStatus.PENDING,
        state.statusByCall,
      );
    },
    getReferralOwnProfileSuccess(state, action: PayloadAction<ReferralProfileResponse>) {
      state.referralProfile = toReferralProfile(action.payload);
      state.isReferralProfileResolved = true;
      setCallStatus(
        CRMCall.getReferralOwnProfile,
        'crm/getReferralOwnProfile',
        CallStatus.READY,
        state.statusByCall,
      );
    },
    getReferralOwnProfileFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      // A 404 is expected - it just means the user has no referral profile yet.
      state.referralProfile = null;
      state.isReferralProfileResolved = true;
      setCallStatus(
        CRMCall.getReferralOwnProfile,
        'crm/getReferralOwnProfile',
        CallStatus.ERROR,
        state.statusByCall,
        action.payload?.status,
        state.errorsLog,
      );
    },
    getReferralOwnQr(state) {
      setCallStatus(CRMCall.getReferralOwnQr, 'crm/getReferralOwnQr', CallStatus.PENDING, state.statusByCall);
    },
    getReferralOwnQrSuccess(state, action: PayloadAction<ReferralQrCode>) {
      state.referralQrCode = action.payload;
      setCallStatus(CRMCall.getReferralOwnQr, 'crm/getReferralOwnQr', CallStatus.READY, state.statusByCall);
    },
    getReferralOwnQrFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      // A 404 just means the QR is disabled for this referrer.
      state.referralQrCode = null;
      setCallStatus(
        CRMCall.getReferralOwnQr,
        'crm/getReferralOwnQr',
        CallStatus.ERROR,
        state.statusByCall,
        action.payload?.status,
        state.errorsLog,
      );
    },
    getReferralOwnReferredUsers(state) {
      setCallStatus(CRMCall.getReferralOwnReferredUsers, 'crm/getReferralOwnReferredUsers', CallStatus.PENDING, state.statusByCall);
    },
    getReferralOwnReferredUsersSuccess(state, action: PayloadAction<ReferralPagedResponse<ReferralReferredUser>>) {
      state.referredUsers = action.payload?.content ?? [];
      setCallStatus(CRMCall.getReferralOwnReferredUsers, 'crm/getReferralOwnReferredUsers', CallStatus.READY, state.statusByCall);
    },
    getReferralOwnReferredUsersFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      state.referredUsers = [];
      setCallStatus(
        CRMCall.getReferralOwnReferredUsers,
        'crm/getReferralOwnReferredUsers',
        CallStatus.ERROR,
        state.statusByCall,
        action.payload?.status,
        state.errorsLog,
      );
    },
    getReferralOwnCommissions(state) {
      setCallStatus(CRMCall.getReferralOwnCommissions, 'crm/getReferralOwnCommissions', CallStatus.PENDING, state.statusByCall);
    },
    getReferralOwnCommissionsSuccess(state, action: PayloadAction<ReferralPagedResponse<ReferralCommissionEntry>>) {
      state.referralCommissions = action.payload?.content ?? [];
      setCallStatus(CRMCall.getReferralOwnCommissions, 'crm/getReferralOwnCommissions', CallStatus.READY, state.statusByCall);
    },
    getReferralOwnCommissionsFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      state.referralCommissions = [];
      setCallStatus(
        CRMCall.getReferralOwnCommissions,
        'crm/getReferralOwnCommissions',
        CallStatus.ERROR,
        state.statusByCall,
        action.payload?.status,
        state.errorsLog,
      );
    },
    createReferralOwnPayoutRequest(state, action: PayloadAction<CreateReferralPayoutRequest>) {
      state.requestedPayoutAmount = null;
      setCallStatus(
        CRMCall.createReferralOwnPayoutRequest,
        'crm/createReferralOwnPayoutRequest',
        CallStatus.PENDING,
        state.statusByCall,
      );
    },
    createReferralOwnPayoutRequestSuccess(state, action: PayloadAction<ReferralPayoutRequest>) {
      state.requestedPayoutAmount = toRequestedPayoutAmount(action.payload);
      setCallStatus(CRMCall.createReferralOwnPayoutRequest, 'crm/createReferralOwnPayoutRequest', CallStatus.READY, state.statusByCall);
    },
    createReferralOwnPayoutRequestFailed(state, action: PayloadAction<ErrorPayloadFull>) {

      setCallStatus(
        CRMCall.createReferralOwnPayoutRequest,
        'crm/createReferralOwnPayoutRequest',
        CallStatus.ERROR,
        state.statusByCall,
        action.payload?.status,
        state.errorsLog,
      );
    },
    setUseDemoAccount(state, action: PayloadAction<boolean>) {
      state.isUseDemoAccount = !!getDemoTradingAccount(state.tradingAccounts) && action.payload;
    },
    switchAccount(state, action: PayloadAction<boolean>) {
      state.isUseDemoAccount = !!getDemoTradingAccount(state.tradingAccounts) && action.payload;
      state.isSwitchingAccount = true;
    },
    setIsSwitchingAccount(state, action: PayloadAction<boolean>) {
      state.isSwitchingAccount = action.payload;
    },
    setInitiallySelectedPlanId(state, action) {
      state.enroll.initiallySelectedPlanId = action.payload;
    },
    setInitiallySelectedAutoPass(state, action) {
      state.enroll.initiallySelectedAutoPass = action.payload;
    },
    setInitiallySelectedReferralCode(state, action) {
      state.enroll.initiallySelectedReferralCode = action.payload;
    },
    accountsCompleted(state, action: PayloadAction<AccountsCompletedPayload>) {
      const { referenceIds, accounts } = action.payload;
      // Derived from the full response, before the keep rules below drop the older accounts.
      state.accountHistory = toAccountHistory(accounts);
      const receivedAccounts = accounts.map(toDetailedAccount);
      const cashAccounts = receivedAccounts.filter(account => account.is_cash_account);
      const tradingAccounts = receivedAccounts.filter(account => !account.is_cash_account);
      // Only the latest real trading account is kept - the older ones (closed plans) are dropped.
      // Cash and demo accounts are not affected.
      const latestTradingAccount = getLatestTradingAccount(tradingAccounts);
      const shouldKeepAccount = (referenceId: string): boolean => (
        isDemoAccountReference(referenceId) || referenceId === latestTradingAccount?.reference_id
      );
      const tradingAccountReferenceIds = referenceIds.filter(shouldKeepAccount);

      // `selectedAccount` defaults to the first entry, so the funded account is kept in front of the
      // demo one instead of relying on the order the response happens to have.
      state.tradingAccounts = [
        ...tradingAccountReferenceIds.filter(referenceId => !isDemoAccountReference(referenceId)),
        ...tradingAccountReferenceIds.filter(isDemoAccountReference),
      ];
      state.detailedAccounts = {
        cashAccounts,
        tradingAccounts: tradingAccounts.filter(account => shouldKeepAccount(account.reference_id)),
      };
      state.cashAccounts = cashAccounts.map(account => account.reference_id);

      if (hasAccountOnlyDemoPlan(state)) {
        state.isUseDemoAccount = true;
      }

      if (state.selectedAccount > state.tradingAccounts.length - 1) state.selectedAccount = 0;
      state.pendingPurchasedPlanId = null;
      setCRMCallStatus(CRMCall.getAccounts, CallStatus.READY, state);
    },
    /**
     * The account an `AccountCreated` event describes, kept next to the real ones so the plan can be
     * rendered while it is still being created. It is replaced by the accounts call once the account
     * becomes usable. The same keep rules as `accountsCompleted` apply - demo plus the newest funded.
     */
    setPendingTradingAccount(state: CRMState, action: PayloadAction<NullableString>) {
      state.pendingTradingAccountReference = action.payload;
    },
    accountCreatedEventReceived(state: CRMState, action: PayloadAction<DetailedAccount>) {
      const account = action.payload;
      const { cashAccounts, tradingAccounts } = state.detailedAccounts;

      if (tradingAccounts.some(({ reference_id }) => reference_id === account.reference_id)) {
        return;
      }

      const mergedAccounts = [ ...tradingAccounts, account ];
      const latestTradingAccount = getLatestTradingAccount(mergedAccounts);
      const keptAccounts = mergedAccounts.filter(({ reference_id }) => (
        isDemoAccountReference(reference_id) || reference_id === latestTradingAccount?.reference_id
      ));
      const keptReferenceIds = keptAccounts.map(({ reference_id }) => reference_id);

      state.detailedAccounts = { cashAccounts, tradingAccounts: keptAccounts };
      state.tradingAccounts = [
        ...keptReferenceIds.filter(referenceId => !isDemoAccountReference(referenceId)),
        ...keptReferenceIds.filter(isDemoAccountReference),
      ];
    },
    getTradingPlatforms(state) {
      state.tradingPlatformId = undefined;
      setCRMCallStatus(CRMCall.getTradingPlatforms, CallStatus.PENDING, state);
    },
    getTradingPlatformsSuccess(state, action: PayloadAction<TradingPlatform[]>) {
      const items = action.payload;
      const firstPlatform = items.find(({ trading_platform_name }) => trading_platform_name === 'Hammer Lite');
      state.tradingPlatformId = firstPlatform?.trading_platform_id;
      setCRMCallStatus(CRMCall.getTradingPlatforms, CallStatus.READY, state);
    },
    getTradingPlatformsFailed(state, action) {
      const { status } = action.payload;
      state.tradingPlatformId = null;
      setCRMCallStatus(CRMCall.getTradingPlatforms, CallStatus.ERROR, state, status);
      appendToErrorsLog(`Getting Trading Platforms failed:\n${JSON.stringify(action.payload)}`, state);
    },
    getCountryList(state) {
      if (!CALLS_TO_MEMOIZE.getCountryList || !isCallStatusReady(CRMCall.getCountryList, state.statusByCall)) {
        setCRMCallStatus(CRMCall.getCountryList, CallStatus.PENDING, state);
      }
    },
    getCountryListFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      setCRMCallStatus(CRMCall.getCountryList, CallStatus.ERROR, state, action.payload.status);
    },
    setBankAccountDetailsOnCreate(state, action) {
      state.enroll.bankAccountDetailsOnCreate.account_info = action.payload;
    },
    activateDemoAccount(state: CRMState) {
      setCRMCallStatus(CRMCall.activateDemoAccount, CallStatus.PENDING, state);
    },
    activateDemoAccountSuccess(state: CRMState) {
      setCRMCallStatus(CRMCall.activateDemoAccount, CallStatus.READY, state);
    },
    activateDemoAccountFail(state: CRMState, action: PayloadAction<ErrorPayloadFull>) {
      const { error, errorData } = action.payload;
      setCRMCallStatus(CRMCall.activateDemoAccount, CallStatus.ERROR, state);
      state.error = error;
    },
    updateIndividualTransitionFromNewToForVerification(state: CRMState) {
      setCRMCallStatus(CRMCall.updateIndividualTransitionFromNewToForVerification, CallStatus.PENDING, state);
    },
    updateIndividualTransitionFromNewToForVerificationSuccess(
      state: CRMState,
    ) {
      setCRMCallStatus(CRMCall.updateIndividualTransitionFromNewToForVerification, CallStatus.READY, state);
    },
    updateIndividualTransitionFromNewToForVerificationFail(state: CRMState, action: PayloadAction<ErrorPayloadFull>) {
      const { error, errorData } = action.payload;
      setCRMCallStatus(CRMCall.updateIndividualTransitionFromNewToForVerification, CallStatus.ERROR, state);
      state.error = error;
    },
    modifyOwnBirthdate(state: CRMState, _action: PayloadAction<ModifyOwnBirthdateRequest>) {
      setCRMCallStatus(CRMCall.modifyOwnBirthdate, CallStatus.PENDING, state);
    },
    modifyOwnBirthdateSuccess(state: CRMState, action: PayloadAction<string>) {
      if (state.individualExtendedInfo) {
        state.individualExtendedInfo.birth_date = action.payload;
      }
      setCRMCallStatus(CRMCall.modifyOwnBirthdate, CallStatus.READY, state);
    },
    modifyOwnBirthdateFail(state: CRMState, action: PayloadAction<ErrorPayloadFull>) {
      const { error, status } = action.payload;
      setCRMCallStatus(CRMCall.modifyOwnBirthdate, CallStatus.ERROR, state, status);
      state.error = error;
    },
    searchTradingPlans(state: CRMState, action: PayloadAction<SearchTradingPlansRequest>) {
      setCRMCallStatus(CRMCall.searchTradingPlans, CallStatus.PENDING, state);
    },
    searchTradingPlansSuccess(state: CRMState, action: PayloadAction<any>) {
      // Check if the response contains a flow array (when using work_flow_depth parameter)
      // If so, extract the plans from the flow array, otherwise use the response directly
      let plans: TradingPlan[] = [];
      if (Array.isArray(action.payload) && action.payload.length > 0 && action.payload[0].flow) {
        plans = action.payload[0].flow;
      } else {
        plans = action.payload;
      }
      state.tradingPlans = Array.isArray(plans) ? plans.map(toTradingPlan) : plans;
      setCRMCallStatus(CRMCall.searchTradingPlans, CallStatus.READY, state);
    },
    searchTradingPlansFail(state: CRMState, action: PayloadAction<ErrorPayloadFull>) {
      const { error } = action.payload;
      setCRMCallStatus(CRMCall.searchTradingPlans, CallStatus.ERROR, state);
      state.error = error;
    },

    searchPlanDetails(state: CRMState, action: PayloadAction<SearchTradingPlansRequest>) {
      setCRMCallStatus(CRMCall.searchPlanDetails, CallStatus.PENDING, state);
    },
    searchPlanDetailsSuccess(state: CRMState, action: PayloadAction<any>) {
      const plans = Array.isArray(action.payload) && action.payload.length > 0 && action.payload[0].flow
        ? action.payload[0].flow
        : action.payload;
      state.planDetailsFlow = Array.isArray(plans) ? plans.map(toTradingPlan) : plans;
      setCRMCallStatus(CRMCall.searchPlanDetails, CallStatus.READY, state);
    },
    searchPlanDetailsFail(state: CRMState, action: PayloadAction<ErrorPayloadFull>) {
      const { error } = action.payload;
      setCRMCallStatus(CRMCall.searchPlanDetails, CallStatus.ERROR, state);
      state.error = error;
    },

    setAutoPassEnabled(state: CRMState, action: PayloadAction<boolean>) {
      state.isAutoPassEnabled = action.payload;
    },
    getAutoPass(state: CRMState) {
      setCRMCallStatus(CRMCall.clientSettingsTradingPlan, CallStatus.PENDING, state);
    },
    getAutoPassSuccess(state: CRMState, action: PayloadAction<any>) {
      const payload = action.payload;
      if (payload) {
        const isAutoPassEnabled: boolean | undefined = payload.is_auto_pass_enabled;
        state.isAutoPassEnabled = isAutoPassEnabled ?? false;
      } else {
        state.isAutoPassEnabled = false;
      }
      setCRMCallStatus(CRMCall.clientSettingsTradingPlan, CallStatus.READY, state);
    },
    getAutoPassFail(state: CRMState, action: PayloadAction<ErrorPayloadFull>) {
      const { error } = action.payload;
      setCRMCallStatus(CRMCall.clientSettingsTradingPlan, CallStatus.ERROR, state);
      state.isAutoPassEnabled = false;
      state.error = error;
    },
    updateAutoPass(state: CRMState, action: PayloadAction<AutoPassRequest>) {
      setCRMCallStatus(CRMCall.clientSettingsTradingPlan, CallStatus.PENDING, state);
    },
    updateAutoPassSuccess(state: CRMState, action: PayloadAction<any>) {
      const payload = action.payload;
      if (payload) {
        const status = payload.status;
        if (status === 200) {
          const currentAutoPassEnabled = state.isAutoPassEnabled;
          state.isAutoPassEnabled = !currentAutoPassEnabled;
        }
      }
      setCRMCallStatus(CRMCall.clientSettingsTradingPlan, CallStatus.READY, state);
    },
    updateAutoPassFail(state: CRMState, action: PayloadAction<ErrorPayloadFull>) {
      const { error } = action.payload;
      setCRMCallStatus(CRMCall.clientSettingsTradingPlan, CallStatus.ERROR, state);
      state.error = error;
      state.isAutoPassEnabled = false;
    },
    createPlanPayment(state: CRMState, action) {
      state.paymentResponseData = { gateway_url: null, payment_reference: null, data: null, raw_data: null };
      setCRMCallStatus(CRMCall.createPlanPayment, CallStatus.PENDING, state);
    },
    createPlanPaymentSuccess(state: CRMState, action: PayloadAction<CreatePlanPaymentResponse>) {
      const { gateway_url, payment_reference, data, raw_data } = action.payload;
      state.paymentResponseData.gateway_url = gateway_url;
      state.paymentResponseData.payment_reference = payment_reference;
      state.paymentResponseData.data = data;
      state.paymentResponseData.raw_data = raw_data ?? null;
      setCRMCallStatus(CRMCall.createPlanPayment, CallStatus.READY, state);
    },
    createPlanPaymentFail(state: CRMState, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setCRMCallStatus(CRMCall.createPlanPayment, CallStatus.ERROR, state, status);
    },
    createPurchasePayment(state: CRMState, action) {
      state.paymentResponseData = { gateway_url: null, payment_reference: null, data: null, raw_data: null };
      setCRMCallStatus(CRMCall.createPurchasePayment, CallStatus.PENDING, state);
    },
    createPurchasePaymentSuccess(state: CRMState, action: PayloadAction<CreatePlanPaymentResponse>) {
      const { gateway_url, payment_reference, data, raw_data } = action.payload;
      state.paymentResponseData.gateway_url = gateway_url;
      state.paymentResponseData.payment_reference = payment_reference;
      state.paymentResponseData.data = data;
      state.paymentResponseData.raw_data = raw_data ?? null;
      setCRMCallStatus(CRMCall.createPurchasePayment, CallStatus.READY, state);
    },
    createPurchasePaymentFail(state: CRMState, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setCRMCallStatus(CRMCall.createPurchasePayment, CallStatus.ERROR, state, status);
    },
    setPendingPurchasedPlanId(state: CRMState, action: PayloadAction<number | null>) {
      state.pendingPurchasedPlanId = action.payload;
    },
    couponPreview(state: CRMState, action) {
      state.couponPreviewData = null;
      state.couponPreviewError = null;
      setCRMCallStatus(CRMCall.couponPreview, CallStatus.PENDING, state);
    },
    couponPreviewSuccess(state: CRMState, action: PayloadAction<CouponPreviewResponse>) {
      state.couponPreviewData = action.payload;
      state.couponPreviewError = null;
      setCRMCallStatus(CRMCall.couponPreview, CallStatus.READY, state);
    },
    couponPreviewFail(state: CRMState, action: PayloadAction<ErrorPayloadFull>) {
      const { status, errorData } = action.payload;
      state.couponPreviewData = null;
      state.couponPreviewError = errorData?.data?.errors?.Error?.[0] ?? 'COUPON_ERROR';
      setCRMCallStatus(CRMCall.couponPreview, CallStatus.ERROR, state, status);
    },
    resetCouponPreview(state: CRMState) {
      state.couponPreviewData = null;
      state.couponPreviewError = null;
      setCRMCallStatus(CRMCall.couponPreview, CallStatus.INITIAL, state);
    },
  },
  extraReducers: {
    [getCountryListSuccess.type]: (state, action) => {
      setCRMCallStatus(CRMCall.getCountryList, CallStatus.READY, state);
    },
    [getIndividualExtendedInfo.type](state) {
      setCRMCallStatus(CRMCall.getIndividualExtendedInfo, CallStatus.PENDING, state);
    },
    [individualExtendedInfoSuccess.type]: (state, action) => {
      state.individualOrCompanyEmployeeId = action.payload?.id;
      state.individualExtendedInfo = action.payload;
      state.connected = true;
      setCRMCallStatus(CRMCall.getIndividualExtendedInfo, CallStatus.READY, state);
      setCRMCallStatus(CRMCall.getAccounts, CallStatus.PENDING, state);
    },
    [getAuthUserTradingAcount.type]: state => {
      setCRMCallStatus(CRMCall.getAccounts, CallStatus.PENDING, state);
    },
    [individualExtendedInfoFailed.type]: (state, action) => {
      state.connected = false;
      setCRMCallStatus(
        CRMCall.getIndividualExtendedInfo,
        CallStatus.ERROR,
        state,
        JSON.stringify(action.payload) as any,
      );
    },
    [accountsFailed.type]: (state, action) => {
      state.tradingAccounts = [];
      processErrorInReducer(state, action);
      setCRMCallStatus(CRMCall.getAccounts, CallStatus.ERROR, state, (action.payload as ErrorPayloadFull).status);
    },
    [logout.type]: state => ({
      ...INITIAL_STATE,
      errorsLog: state.errorsLog,
    }),
  },
});

export const {
  getReferralOwnProfile,
  getReferralOwnProfileSuccess,
  getReferralOwnProfileFailed,
  getReferralOwnQr,
  getReferralOwnQrSuccess,
  getReferralOwnQrFailed,
  getReferralOwnReferredUsers,
  getReferralOwnReferredUsersSuccess,
  getReferralOwnReferredUsersFailed,
  getReferralOwnCommissions,
  getReferralOwnCommissionsSuccess,
  getReferralOwnCommissionsFailed,
  createReferralOwnPayoutRequest,
  createReferralOwnPayoutRequestSuccess,
  createReferralOwnPayoutRequestFailed,
  setIsSwitchingAccount,
  setInitiallySelectedPlanId,
  setInitiallySelectedAutoPass,
  setInitiallySelectedReferralCode,
  setUseDemoAccount,
  switchAccount,
  accountsCompleted,
  accountCreatedEventReceived,
  setPendingTradingAccount,
  getTradingPlatforms,
  getTradingPlatformsSuccess,
  getTradingPlatformsFailed,
  getCountryList,
  getCountryListFailed,
  setBankAccountDetailsOnCreate,
  activateDemoAccount,
  activateDemoAccountSuccess,
  activateDemoAccountFail,
  updateIndividualTransitionFromNewToForVerification,
  updateIndividualTransitionFromNewToForVerificationSuccess,
  updateIndividualTransitionFromNewToForVerificationFail,
  modifyOwnBirthdate,
  modifyOwnBirthdateSuccess,
  modifyOwnBirthdateFail,
  searchTradingPlans,
  searchTradingPlansSuccess,
  searchTradingPlansFail,
  searchPlanDetails,
  searchPlanDetailsSuccess,
  searchPlanDetailsFail,
  setAutoPassEnabled,
  getAutoPass,
  getAutoPassSuccess,
  getAutoPassFail,
  updateAutoPass,
  updateAutoPassSuccess,
  updateAutoPassFail,
  createPlanPayment,
  createPlanPaymentSuccess,
  createPlanPaymentFail,
  createPurchasePayment,
  createPurchasePaymentSuccess,
  createPurchasePaymentFail,
  couponPreview,
  couponPreviewSuccess,
  couponPreviewFail,
  resetCouponPreview,
  setPendingPurchasedPlanId,
} = crmSlice.actions;

export default crmSlice.reducer;
